/*
 * ==============================
 * cryptoUtils.js - Encryption Utilities
 * Handles both Standard and Pro Secure (E2EE) encryption
 * ==============================
 */

// import { db } from '@/managers/indexedDB/indexedDB'; // Removed IndexedDB import

const CRYPTO_CONSTANTS = {
    SALT_LENGTH: 16,
    IV_LENGTH: 12,
    AUTH_TAG_LENGTH: 16,
    ITERATION_COUNT: 100000,
    KEY_LENGTH: 256,
    E2EE_CODE_LENGTH: 6,
    MIN_ENCRYPTED_LENGTH: 24, // IV (12) + min data (4) + AUTH_TAG (16)
    KEY_VERIFICATION_TEXT: 'SHELBULA_KEY_VERIFICATION_V1'
};

// API key patterns for validation
const API_KEY_PATTERNS = {
    'sk-': true,    // OpenAI
    'sk-a': true,   // Claude
    'AIza': true,   // Gemini
    'sk-or': true   // OpenRouter
};

export class CryptoUtils {
    static _standardKey = null;
    static _e2eeKey = null;
    static _isInitializing = false;
    static _verificationCache = new Map();

    // Add static interface for IndexedDB layer
    static async encrypt(data, userId, force = false) {
        await CryptoUtils.waitForEncryption();
        if (!CryptoUtils._standardKey) {
            throw new Error('Encryption not initialized');
        }
        return CryptoUtils._encryptWithKey(data, CryptoUtils._standardKey, force);
    }

    static async decrypt(encryptedData, userId) {
        await CryptoUtils.waitForEncryption();
        if (!CryptoUtils._standardKey) {
            throw new Error('Encryption not initialized');
        }
        return CryptoUtils._decryptWithKey(encryptedData, CryptoUtils._standardKey);
    }

    /**
     * Checks if encryption is initialized
     */
    static isInitialized() {
        return !!this._standardKey;
    }

    /**
     * Verifies if data appears to be encrypted
     */
    static isEncrypted(data) {
        if (typeof data !== 'string') return false;
        
        // First check if it's a known API key pattern
        for (const prefix of Object.keys(API_KEY_PATTERNS)) {
            if (data.startsWith(prefix)) return false;
        }
        
        // Then check for encryption pattern
        return data.length >= CRYPTO_CONSTANTS.MIN_ENCRYPTED_LENGTH &&
               /^[A-Za-z0-9+/=]+$/.test(data) &&
               !data.includes(' ');  // encrypted data never contains spaces
    }

    /**
     * Verifies encryption key is valid for user
     * @param {CryptoKey} key - Key to verify
     * @param {string} userId - User ID
     * @returns {Promise<boolean>} True if key is valid
     */
    static async verifyKey(key, userId) {
        const cacheKey = `${userId}-${key?.algorithm?.name}`;
        
        // Check cache first
        if (this._verificationCache.has(cacheKey)) {
            return this._verificationCache.get(cacheKey);
        }

        try {
            const testEncryption = await this._encryptWithKey(
                CRYPTO_CONSTANTS.KEY_VERIFICATION_TEXT,
                key
            );
            
            const testDecryption = await this._decryptWithKey(testEncryption, key);
            const isValid = testDecryption === CRYPTO_CONSTANTS.KEY_VERIFICATION_TEXT;
            
            // Cache the result
            this._verificationCache.set(cacheKey, isValid);
            
            return isValid;
        } catch (error) {
            return false;
        }
    }

    /**
     * Initializes encryption for a user session
     * @param {string} userId 
     * @param {string} serverSalt 
     */
    static async initializeEncryption(userId, serverSalt) {
        if (!userId || !serverSalt) {
            throw new Error('User ID and server salt required for initialization');
        }

        try {
            this._isInitializing = true;
            this._verificationCache.clear(); // Clear cache on new initialization
            
            // Generate and store the standard encryption key
            const newKey = await this._generateStandardKey(userId, serverSalt);
            
            // Verify the key before storing
            const isValid = await this.verifyKey(newKey, userId);
            if (!isValid) {
                throw new Error('Generated key verification failed');
            }
            
            this._standardKey = newKey;
            
            // REMOVED: await this._storeKeyVerification(userId, serverSalt);
            // This step was redundant as verifyKey already confirms the key's usability,
            // and we are no longer persisting this specific verification artifact to a DB.
            // Its removal helps _isInitializing become false sooner.
            
            return true;
        } catch (error) {
            this._standardKey = null;
            this._verificationCache.clear();
            throw new Error(`Failed to initialize encryption: ${error.message}`);
        } finally {
            this._isInitializing = false;
        }
    }

    /**
     * Verifies and restores encryption keys from storage
     * @param {string} userId 
     * @param {string} serverSalt 
     */
    static async verifyAndRestoreKeys(userId, serverSalt) {
        try {
            // const storedVerification = await db.getUserMeta(userId); // Removed IndexedDB call
            const storedVerification = null; // Assume no stored verification without DB

            if (!storedVerification?.keyVerification) {
                // This branch will now likely always be taken
                return await this.initializeEncryption(userId, serverSalt);
            }

            // Generate key for verification
            const newKey = await this._generateStandardKey(userId, serverSalt);
            
            // Verify the key directly
            const isValid = await this.verifyKey(newKey, userId);
            if (!isValid) {
                return await this.initializeEncryption(userId, serverSalt);
            }

            // Double-check with stored verification
            const verificationText = await this._encryptWithKey(
                CRYPTO_CONSTANTS.KEY_VERIFICATION_TEXT,
                newKey
            );

            // This comparison will likely fail as storedVerification.keyVerification is unavailable
            if (verificationText === storedVerification.keyVerification) {
                this._standardKey = newKey;
                
                // Restore E2EE key if it exists (this part will also not run as intended without storedVerification)
                if (storedVerification.e2eeEnabled && storedVerification.e2eeVerification) {
                    // await this._restoreE2EEKey(userId, storedVerification.e2eeVerification); // Assuming _restoreE2EEKey might also need adjustment or is handled elsewhere
                }
                
                return true;
            }

            return await this.initializeEncryption(userId, serverSalt);
        } catch (error) {
            this._standardKey = null;
            this._verificationCache.clear();
            // It's often better to let the original error propagate or re-throw a more specific one
            // For now, retaining original behavior of throwing a generic message.
            throw new Error('Failed to verify encryption keys');
        }
    }

    /**
     * Encrypts data with specified key
     * @private
     */
    static async _encryptWithKey(data, key, force = false) {
        if (!key) {
            throw new Error('Encryption key required');
        }

        if (!force && this.isEncrypted(data)) {
            // If data is a string and already looks encrypted, return it as is.
            // If it's an object, it's harder to tell, so proceed with caution or always re-encrypt if force is true.
            // For now, throwing an error is consistent with previous logic.
            throw new Error('Data appears to be already encrypted');
        }

        const encoder = new TextEncoder();
        const iv = crypto.getRandomValues(new Uint8Array(CRYPTO_CONSTANTS.IV_LENGTH));
        const encodedData = encoder.encode(
            typeof data === 'string' ? data : JSON.stringify(data)
        );

        try {
            const encryptedContent = await crypto.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv: iv,
                    tagLength: CRYPTO_CONSTANTS.AUTH_TAG_LENGTH * 8
                },
                key,
                encodedData
            );

            const combinedArray = new Uint8Array(iv.length + encryptedContent.byteLength);
            combinedArray.set(iv, 0);
            combinedArray.set(new Uint8Array(encryptedContent), iv.length);

            let binaryString = '';
            for (let i = 0; i < combinedArray.length; i++) {
                binaryString += String.fromCharCode(combinedArray[i]);
            }

            const result = btoa(binaryString);
            return result;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Decrypts data with specified key
     * @private
     */
    static async _decryptWithKey(encryptedData, key) {
        if (!key) {
            throw new Error('Decryption key required');
        }

        if (!this.isEncrypted(encryptedData)) {
            // If data doesn't look encrypted, it might be plain text already.
            // Depending on policy, either return as is, or throw error.
            // Current behavior is to throw, maintaining consistency.
            throw new Error('Data does not appear to be encrypted');
        }

        try {
            const encryptedArray = new Uint8Array(
                atob(encryptedData)
                    .split('')
                    .map(char => char.charCodeAt(0))
            );

            if (encryptedArray.length < CRYPTO_CONSTANTS.MIN_ENCRYPTED_LENGTH) {
                throw new Error('Encrypted data is too short');
            }

            const iv = encryptedArray.slice(0, CRYPTO_CONSTANTS.IV_LENGTH);
            const data = encryptedArray.slice(CRYPTO_CONSTANTS.IV_LENGTH);

            const decryptedContent = await crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: iv,
                    tagLength: CRYPTO_CONSTANTS.AUTH_TAG_LENGTH * 8
                },
                key,
                data
            );

            const decoder = new TextDecoder();
            const decryptedText = decoder.decode(decryptedContent);

            try {
                const parsed = JSON.parse(decryptedText);
                return parsed;
            } catch {
                return decryptedText;
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cleans up encryption state
     */
    static clearKeys() {
        this._standardKey = null;
        this._e2eeKey = null;
        this._isInitializing = false;
        this._verificationCache.clear();
    }

    /**
     * Waits for encryption to be initialized
     * @param {number} timeout - Timeout in milliseconds
     */
    static async waitForEncryption(timeout = 5000) {
        const start = Date.now();
        
        while ((!this.isInitialized() || this._isInitializing) && Date.now() - start < timeout) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!this.isInitialized()) {
            throw new Error('Encryption initialization timeout');
        }
    }

    /**
     * Validates if data can be encrypted
     * @param {any} data - Data to validate
     * @throws {Error} If data is invalid
     */
    static validateForEncryption(data) {
        if (data === undefined || data === null) {
            throw new Error('Cannot encrypt undefined or null data');
        }

        if (this.isEncrypted(data)) {
            throw new Error('Data appears to be already encrypted');
        }

        if (typeof data !== 'string' && typeof data !== 'object') {
            throw new Error('Data must be string or object for encryption');
        }

        return true;
    }

    /**
     * Generates standard encryption key
     * @private 
     */
    static async _generateStandardKey(userId, serverSalt) {
        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            encoder.encode(`${userId}:${serverSalt}`),
            { name: 'PBKDF2' },
            false,
            ['deriveBits', 'deriveKey']
        );

        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: encoder.encode('standard-encryption-salt'),
                iterations: CRYPTO_CONSTANTS.ITERATION_COUNT,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: 'AES-GCM', length: CRYPTO_CONSTANTS.KEY_LENGTH },
            true,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Stores key verification data
     * @private
     */
    static async _storeKeyVerification(userId, serverSalt) {
        if (!this._standardKey) {
            throw new Error('No encryption key available for verification');
        }

        try {
            const verificationText = await this._encryptWithKey(
                CRYPTO_CONSTANTS.KEY_VERIFICATION_TEXT,
                this._standardKey
            );

            // await db.setUserMeta(userId, { // Removed IndexedDB call
            //     keyVerification: verificationText,
            //     salt: serverSalt,
            //     lastVerified: Date.now()
            // });

            // Since we are not storing to DB, this function effectively just ensures
            // the key can encrypt the verification text.
            // If an error occurs during _encryptWithKey, it will be thrown.
            return true;
        } catch (error) {
            // Re-throw the error to be handled by the caller (e.g., initializeEncryption)
            throw error;
        }
    }
}

export default CryptoUtils;